import { useCallback, useEffect, useState } from 'react'

// Safari before 16.4, and iPadOS, still name the Fullscreen API with the webkit prefix.
type FullscreenDocument = Document & {
  webkitFullscreenEnabled?: boolean
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}
type FullscreenRoot = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void }

/**
 * Whether the browser can put the page full screen. An iPhone can't (Safari only takes a video element full screen
 * there), so the watch screen shows no button for it rather than one that does nothing.
 */
export function canGoFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  const doc = document as FullscreenDocument
  return doc.fullscreenEnabled === true || doc.webkitFullscreenEnabled === true
}

function fullscreenElement(): Element | null {
  const doc = document as FullscreenDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

/** Whether the page itself is full screen (not some element of it). */
function pageIsFullscreen(): boolean {
  return typeof document !== 'undefined' && fullscreenElement() === document.documentElement
}

function exitFullscreen() {
  const doc = document as FullscreenDocument
  const exit = doc.exitFullscreen ? () => doc.exitFullscreen() : doc.webkitExitFullscreen?.bind(doc)
  // A refusal (the page lost full screen meanwhile) leaves things as they are.
  if (exit) void Promise.resolve(exit()).catch(() => {})
}

/** Leaves full screen if the page is in it: a page that went full screen leaving the screen it was for. */
export function leaveFullscreen() {
  if (pageIsFullscreen()) exitFullscreen()
}

/**
 * The page's full screen, kept current through the browser's own changes (Escape, the browser's exit): `active`, and
 * `toggle`, which puts the page itself full screen, so anything portalled to the body (popovers, tooltips) still shows,
 * and the page lays out its full-screen view (the watch screen's `data-fullscreen`). On Android the navigation bar
 * hides too. Only call `toggle` from a press: browsers refuse full screen without one.
 */
export function useFullscreen(): { active: boolean; toggle: () => void } {
  const [active, setActive] = useState(pageIsFullscreen)
  useEffect(() => {
    const sync = () => setActive(pageIsFullscreen())
    sync()
    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    return () => {
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
    }
  }, [])
  const toggle = useCallback(() => {
    if (fullscreenElement()) {
      exitFullscreen()
      return
    }
    const root = document.documentElement as FullscreenRoot
    const enter = root.requestFullscreen ? () => root.requestFullscreen({ navigationUI: 'hide' }) : root.webkitRequestFullscreen?.bind(root)
    if (enter) void Promise.resolve(enter()).catch(() => {})
  }, [])
  return { active, toggle }
}
